<template>
  <div
    v-if="recordHref"
    :class="[$style.playerWrapper, playing ? $style.playerWrapperPlaying : '']"
  >
    <div :class="$style.header">
      <div :class="$style.control">
        <button
          :class="$style.playBtn"
          :disabled="mediaError"
          @click="togglePlay"
        >
          <svg
            :class="$style.playIcon"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
          >
            <path d="M4 3.5 12 8l-8 4.5z" />
          </svg>
        </button>
        <span :class="$style.title">
          <slot />
          {{ mediaErrorTitle }}
        </span>
      </div>
      <div
        v-if="firstPlay"
        :class="$style.time"
      >
        <div :class="$style.timeCurrent">
          {{ timePlayed }}
        </div>
        <div :class="$style.timeTotal">
          {{ timeDuration }}
        </div>
      </div>
    </div>
    <div :class="$style.rangeWrapper">
      <input
        ref="range"
        v-model="thumbPosition"
        type="range"
        :class="[$style.range, showPulse ? $style.rangeShowPulse : '']"
        :style="`--seek-before-width: ${thumbPosition}%`"
        max="100"
        step="0.01"
        :disabled="mediaError"
        @click="seekTime"
        @mousedown="startSeekPosition"
      >
      <audio
        ref="audio"
        :src="recordHref"
        preload="metadata"
        :class="$style.audio"
        @timeupdate="update"
        @loadeddata="load"
        @play="playHandler"
        @ended="playing = false"
        @error="mediaError = true"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'AudioPlayer',
  components: {},
  props: {
    recordHref: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      audio: '',
      playing: false,
      currentTime: 0,
      durationTime: 0,
      thumbPosition: 0,
      firstPlay: false,
      seeking: false,
      showPulse: false,
      mediaError: false
    };
  },
  computed: {
    mediaErrorTitle() {
      return this.mediaError ? ` (${this.$trlMessage('media.error')})` : '';
    },
    timePlayed() {
      return this.convertTime(this.currentTime);
    },
    timeDuration() {
      return this.convertTime(this.durationTime);
    }
  },
  watch: {
    playing(value) {
      if (value) {
        return this.$refs.audio.play();
      }
      this.$refs.audio.pause();
    }
  },
  methods: {
    togglePlay() {
      this.playing = !this.playing;
    },
    update(audio) {
      this.currentTime = audio.target.currentTime;
      if (this.seeking) {
        return;
      }
      this.thumbPosition = (this.currentTime / this.durationTime) * 100;
    },
    load() {
      if (this.$refs.audio.readyState >= 2) {
        this.durationTime = this.$refs.audio.duration;
      }
    },
    seekTime(range) {
      this.playing = false;
      this.currentTime = (range.target.value / 100) * this.durationTime;
      this.$refs.audio.currentTime = this.currentTime;

      this.thumbPosition = (this.currentTime / this.durationTime) * 100;
      this.$nextTick(() => {
        this.playing = true;
        this.seeking = false;
      });
    },
    playHandler() {
      this.playing = true;

      if (!this.firstPlay) {
        this.firstPlay = true;
        this.showPulse = true;
        setTimeout(() => {
          this.showPulse = false;
        }, 350);
      }
    },
    startSeekPosition() {
      this.seeking = true;
    },
    convertTime(rawSeconds) {
      const sec = Math.round(rawSeconds);
      const seconds = sec % 60;
      const minutes = ((sec - seconds) % 3600) / 60;
      const hours = (sec - seconds - minutes * 60) / 3600;

      return `${hours ? `${hours}:` : ''}${
        hours ? minutes.toString().padStart(2, '0') : minutes
      }:${seconds.toString().padStart(2, '0')}`;
    }
  }
};
</script>

<style lang="less" module>
@activeColor: #22bbd2;
@inActiveColor: #bfbfbf;
@thumbSize: 5px;

.playerWrapper {
  min-height: 22px;
  --track-color: #050505;
}

.playerWrapperPlaying {
  --track-color: @activeColor;
  .playBtn {
    fill: @activeColor;
  }
}

.header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.control {
  display: flex;
}

.playBtn {
  border: none;
  background-color: transparent;
  cursor: pointer;
  width: 20px;
  height: 20px;
  padding: 0;

  &:hover:not([disabled]) {
    fill: @activeColor;
  }
}

.playIcon {
  width: 20px;
  height: 20px;
}

.title {
  line-height: 1.15;
  padding: 0 2em 0 0;
  overflow: hidden;
  word-wrap: break-word;
  white-space: normal;
}

.titleFailed {
  margin: 2px 0 0 6px;
}

.time {
  font-size: 0.8em;
  line-height: 1.4375;
  color: #606060;
  white-space: nowrap;
  user-select: none;
  display: flex;
  flex-direction: row;
}

.timeCurrent {
  margin-right: 0.65em;
}

.timeCurrent,
.timeTotal {
  min-width: 30px;
}

.rangeWrapper {
  padding-left: 7px;
}

.thumb() {
  height: @thumbSize;
  width: @thumbSize;
  border-radius: @thumbSize;
  background: var(--track-color);
  cursor: pointer;
  border: none;
  margin-top: -2px;
  transition: all 0.5s;
}

.line() {
  width: 100%;
  height: 1px;
  cursor: pointer;
  background: linear-gradient(
    to right,
    var(--track-color) var(--seek-before-width),
    @inActiveColor var(--seek-before-width)
  );
}

.range {
  height: 11px;
  width: 100%;
  margin-top: 3px;
  background-color: transparent;
  display: block;
  cursor: pointer;
  position: relative;
  padding: 10px 0;

  -webkit-appearance: none;
  --seek-before-width: 0%;
  --thumb-size: 4px;
  --thumb-position: -2px;

  &:hover:not([disabled]) {
    --track-color: @activeColor;
    &::-moz-range-thumb {
      box-shadow: 0 0 0 1px @activeColor;
    }
    &::-webkit-slider-thumb {
      box-shadow: 0 0 0 1px @activeColor;
    }
    &::-ms-thumb {
      box-shadow: 0 0 0 1px @activeColor;
    }
  }

  &:focus {
    outline: none;
  }
  // WebKit
  &::-webkit-slider-runnable-track {
    .line();
  }
  &::-webkit-slider-thumb {
    .thumb();
    -webkit-appearance: none;
  }
  // Mozilla
  &::-moz-range-track {
    .line();
  }
  &::-moz-range-thumb {
    .thumb();
  }
  // MS
  &::-ms-track {
    .line();
  }
  &::-ms-fill-lower {
    background: @inActiveColor;
    border: 0 solid #000101;
    border-radius: 0;
  }
  &::-ms-fill-upper {
    background: linear-gradient(
      to right,
      var(--track-color) var(--seek-before-width),
      @inActiveColor var(--seek-before-width)
    );
    border-radius: 0;
  }
  &::-ms-thumb {
    .thumb();
  }
  &:focus::-ms-fill-lower {
    background: @inActiveColor;
  }
  &:focus::-ms-fill-upper {
    background: @inActiveColor;
  }
}

.rangeShowPulse {
  transition: all 0.25s;
  &::-moz-range-thumb {
    box-shadow: 0 0 0 3px @activeColor;
  }
  &::-webkit-slider-thumb {
    box-shadow: 0 0 0 3px @activeColor;
  }
  &::-ms-thumb {
    box-shadow: 0 0 0 3px @activeColor;
  }
}

.audio {
  display: none;
}
</style>

<i18n lang="json">{}</i18n>
